"use client";

import { memo, useCallback, useEffect, useRef, useState } from "react";



import { getAllRelationEntities, updateRelationEntities } from "@/api/entity-relation";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { HashLoader } from "react-spinners";
import { useDebouncedCallback } from "use-debounce";



import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";


interface Props {
  entityId: number;
  isOpen: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 20;

const EntitiesRelationListModal: React.FC<Props> = ({
  isOpen,
  onClose,
  entityId
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>انتخاب ارتباطات</DialogTitle>
        </DialogHeader>

        <Content entityId={entityId} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
};

const Content = memo(
  ({ entityId, onClose }: { entityId: number; onClose: () => void }) => {
    const [search, setSearch] = useState("");
    const [selectedEntities, setSelectedEntities] = useState<number[]>([]);

    const queryClient = useQueryClient();

    const observerRef = useRef<IntersectionObserver | null>(null);

    // infinite query
    const { data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
      useInfiniteQuery({
        queryKey: ["relation-entities", entityId, search],

        queryFn: ({ pageParam = 1 }) =>
          getAllRelationEntities(entityId, {
            size: PAGE_SIZE,
            page: pageParam,
            search
          }),

        initialPageParam: 1,

        getNextPageParam: (lastPage, pages) => {
          const currentPageItems = lastPage?.data ?? [];

          const loadedItems = pages.flatMap((x) => x?.data || []).length;
          const totalCount = lastPage?.totalCount ?? 0;

          // اگر همه دیتا لود شده → stop
          if (loadedItems >= totalCount) return undefined;

          // اگر این صفحه کمتر از PAGE_SIZE بود → صفحه آخر
          if (currentPageItems.length < PAGE_SIZE) return undefined;

          return pages.length + 1;
        }
      });

    // flatten data
    const entities = data?.pages.flatMap((page) => page?.data || []) ?? [];

    // init selected
    useEffect(() => {
      setSelectedEntities((prev) => {
        const selectedIds = entities.filter((x) => x.isAccess).map((x) => x.id);

        return [...new Set([...prev, ...selectedIds])];
      });
    }, [data]);

    // checkbox handler
    const changeHandler = (id: number) => {
      setSelectedEntities((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    };

    // debounce search
    const handleSearch = useDebouncedCallback((value: string) => {
      setSearch(value);
    }, 700);

    // mutation
    const { mutate, isPending } = useMutation({
      mutationFn: () => updateRelationEntities(entityId, selectedEntities),

      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["relation-entities", entityId]
        });

        onClose();
      }
    });

    // infinite scroll observer
    const lastItemRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (isFetchingNextPage || !hasNextPage) return;

        if (observerRef.current) {
          observerRef.current.disconnect();
        }

        observerRef.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage();
          }
        });

        if (node) {
          observerRef.current.observe(node);
        }
      },
      [fetchNextPage, hasNextPage, isFetchingNextPage]
    );

    return (
      <>
        {/* Search */}
        <Input
          placeholder="جستجو"
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full md:w-1/2"
        />

        {/* List */}
        <div className="mt-5 h-64 divide-y divide-slate-300 overflow-y-auto rounded-md border border-slate-300">
          {isFetching && !entities.length ? (
            <div className="flex h-full items-center justify-center">
              <HashLoader color="#0099A5" size={45} />
            </div>
          ) : (
            <>
              {entities.map((entity, index) => {
                const isLast = index === entities.length - 1;

                return (
                  <div
                    key={entity.id}
                    ref={isLast ? lastItemRef : null}
                    className="flex items-center gap-x-2 px-2 py-4"
                  >
                    <Checkbox
                      checked={selectedEntities.includes(entity.id)}
                      onCheckedChange={() => changeHandler(entity.id)}
                    />

                    <p className="line-clamp-1 text-sm">{entity.name}</p>
                  </div>
                );
              })}

              {/* loading next page */}
              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <HashLoader color="#0099A5" size={30} />
                </div>
              )}

              {/* empty */}
              {!entities.length && !isFetching && (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">
                  داده‌ای یافت نشد
                </div>
              )}
            </>
          )}
        </div>

        {/* Save */}
        <Button
          className="mt-5 w-full"
          onClick={() => mutate()}
          disabled={isPending}
        >
          {isPending ? "در حال ذخیره..." : "ذخیره"}
        </Button>
      </>
    );
  }
);

export default EntitiesRelationListModal;